import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { noop } from 'underscore';
import { DragCallbackData, DraggableProps } from 'components/Draggable';
import { startAssetEdit, stopAssetEdit } from 'redux/modules/embed/actions';

type DragCallback = (
  data: DragCallbackData,
  e: React.MouseEvent<HTMLElement>,
) => void;

export type UseDragTrackAssetProps = {
  assetId: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  onDrag?: DragCallback;
  onDragStart?: DragCallback;
  /*
   * onDragStop should probably just be DragCallback, but some work is needed
   * in Draggable to support that and historically the dragData hasn't been
   * needed in the drag stop callback.
   * */
  onDragStop?: (e: React.MouseEvent<HTMLElement>, moved: boolean) => void;
};

export default function useDragTrackAsset(props: UseDragTrackAssetProps) {
  const {
    assetId,
    onClick = noop,
    onDrag = noop,
    onDragStart = noop,
    onDragStop = noop,
  } = props;

  const dispatch = useDispatch();

  const [dragging, setDragging] = useState(false);
  const dragged = useRef(false);

  const handleDrag: DraggableProps['onDrag'] = useCallback(
    (e, dragData) => {
      onDrag(dragData, e);
      dragged.current = true;
    },
    [onDrag],
  );

  const handleDragStart: DraggableProps['onStart'] = useCallback(
    (e, dragData) => {
      dispatch(startAssetEdit(assetId, 'move'));
      dragged.current = false;
      setDragging(true);
      onDragStart(dragData, e);
    },
    [assetId, dispatch, onDragStart],
  );

  const handleDragStop: DraggableProps['onStop'] = useCallback(
    e => {
      dispatch(stopAssetEdit());
      onDragStop(e, dragged.current);
      setDragging(false);

      if (!dragged.current) {
        onClick(e);
      }
    },
    [dispatch, onClick, onDragStop],
  );

  return {
    dragging,
    handleDrag,
    handleDragStart,
    handleDragStop,
  };
}
